import { acceptHMRUpdate, defineStore } from "pinia"

import { api, REQUEST_ABORTED } from "@/logic/api"
import { getConstants } from "@/logic/constants"
import { getI18n } from "@/logic/i18n"
import { exhaustiveCheck } from "@/logic/tsHelpers"
import { useAccountStore } from "@/modules/account/store"
import { cacheCard, clearCardCache, getCachedCard, removeCachedCard } from "@/modules/dashboard/cache"
import type { DashboardCardIdentifier, DashboardCards } from "@/modules/dashboard/types"
import { useAppConfigStore } from "@/store/appConfig"
import { useSnackbarsStore } from "@/store/snackbars"

type DashboardResponse = {
  data: {
    cards: DashboardCards
  }
}

export const useDashboardStore = defineStore("dashboard", {
  state: () => ({
    cards: {} as DashboardCards,
    loadingCardIds: [] as DashboardCardIdentifier[],
    abortController: null as AbortController | null,
  }),
  getters: {
    canViewCard:
      () =>
      (cardId: DashboardCardIdentifier): boolean => {
        const accountStore = useAccountStore()
        const appConfigStore = useAppConfigStore()

        switch (cardId) {
          case getConstants().DASHBOARD.IDENTIFIERS.NEWS_WEEKLY_COUNT:
            return appConfigStore.isModuleVisible("news") && accountStore.rights["news-edit"]
          case getConstants().DASHBOARD.IDENTIFIERS.COURSE_PARTICIPATIONS_WEEKLY_COUNT:
            return appConfigStore.isModuleVisible("courses") && (accountStore.rights["courses-view"] || accountStore.rights["courses-edit"])
          case getConstants().DASHBOARD.IDENTIFIERS.MEDIA_LIBRARY_WEEKLY_COUNT: {
            const hasRights = accountStore.rights["learningmaterials-edit"] || accountStore.rights["learningmaterials-stats"]
            return appConfigStore.isModuleVisible("learningmaterials") && hasRights
          }
          default:
            return exhaustiveCheck(cardId)
        }
      },
    availableCardIds(): DashboardCardIdentifier[] {
      return Object.values(getConstants().DASHBOARD.IDENTIFIERS).filter((cardId) => this.canViewCard(cardId))
    },
    isCardVisible: (state) => (cardId: DashboardCardIdentifier) => state.cards[cardId] !== undefined || state.loadingCardIds.includes(cardId),
    isLoading: (state) => state.loadingCardIds.length > 0,
    isCardLoading: (state) => (cardId: DashboardCardIdentifier) => state.loadingCardIds.includes(cardId),
  },
  actions: {
    cancelLoad(): void {
      this.abortController?.abort()
      this.abortController = null
      this.loadingCardIds = []
    },
    reset(): void {
      this.cancelLoad()
      this.cards = {}
      clearCardCache()
    },
    async load(cardIds: DashboardCardIdentifier[]): Promise<void> {
      const pendingCardIds = [...new Set(cardIds)].filter((cardId) => !this.isCardLoading(cardId))

      if (pendingCardIds.length === 0) {
        return
      }

      const appId = useAppConfigStore().appId!
      const userId = useAccountStore().id!
      // All dashboard requests share cancellation on unmount, login/logout, or app switches.
      this.abortController ??= new AbortController()
      const abortController = this.abortController
      const cacheContext = { appId, userId }

      for (const cardId of pendingCardIds) {
        const cachedPayload = getCachedCard(cacheContext, cardId)

        if (cachedPayload === undefined) {
          delete this.cards[cardId]
          continue
        }

        this.cards[cardId] = cachedPayload
      }

      this.loadingCardIds.push(...pendingCardIds)

      try {
        const response = (await api.post(
          "/dashboard",
          {
            card_ids: pendingCardIds,
          },
          abortController,
        )) as DashboardResponse

        if (abortController.signal.aborted) {
          return
        }

        if (useAppConfigStore().appId !== appId || useAccountStore().id !== userId) {
          this.reset()
          return
        }

        const loadedCards = response.data.cards

        for (const cardId of pendingCardIds) {
          const loadedPayload = loadedCards[cardId]

          if (loadedPayload === undefined) {
            delete this.cards[cardId]
            removeCachedCard(cacheContext, cardId)
            continue
          }

          this.cards[cardId] = loadedPayload
          cacheCard(cacheContext, cardId, loadedPayload)
        }
      } catch (error) {
        if (abortController.signal.aborted || error === REQUEST_ABORTED) {
          return
        }

        for (const cardId of pendingCardIds) {
          delete this.cards[cardId]
          removeCachedCard(cacheContext, cardId)
        }

        // Avoid expanding vue-i18n's recursive message types for this plain translation.
        const i18n = getI18n() as unknown as { t: (translationKey: string) => string }
        useSnackbarsStore().error(i18n.t("global.dataLoadFailure"))
      } finally {
        if (!abortController.signal.aborted) {
          this.loadingCardIds = this.loadingCardIds.filter((cardId) => !pendingCardIds.includes(cardId))
        }
      }
    },
  },
})

if (import.meta.hot) {
  // @ts-ignore
  import.meta.hot.accept(acceptHMRUpdate(useDashboardStore, import.meta.hot))
}
